/**
 * Входной тест уровня: 10–15 вопросов от A1 к C1, задаёт стартовый трек
 * изучаемого языка (`UserLanguage.currentLevel`). У каждого языка свой банк
 * вопросов; подсказка к вопросу — на языке интерфейса.
 *
 * Правило размещения: ученик стоит на самой высокой ступени, которую он
 * прошёл, если пройдены и все ступени ниже. «Перепрыгнуть» проваленную
 * ступень угадыванием сверху нельзя — дыра в основе важнее удачи на C1.
 * Ни одной ступени не прошёл — начинает с A1.
 */

import { z } from 'zod';

import { CURRICULUM_LEVELS, CurriculumLevelSchema, type CurriculumLevel } from './curriculum';

export const PLACEMENT_MIN_QUESTIONS = 10;
export const PLACEMENT_MAX_QUESTIONS = 15;
/** Доля верных ответов ступени, с которой она считается пройденной. */
export const PLACEMENT_PASS_RATIO = 0.6;

/** Вопрос теста: предложение с пропуском `___` и варианты. */
export const PlacementQuestionSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9-]{1,32}$/),
    level: CurriculumLevelSchema,
    sentence: z.string().trim().min(5).max(200).includes('___'),
    /**
     * Подсказка — что нужно выбрать: по-русски и по-английски. Остальным
     * языкам интерфейса показывается английская.
     */
    prompts: z.object({ ru: z.string().trim().min(2).max(160), en: z.string().trim().min(2).max(160) }),
    options: z.array(z.string().trim().min(1).max(60)).min(3).max(4),
    answerIndex: z.number().int().nonnegative(),
  })
  .refine((question) => question.answerIndex < question.options.length, {
    message: 'answerIndex вне списка вариантов',
    path: ['answerIndex'],
  });
export type PlacementQuestion = z.infer<typeof PlacementQuestionSchema>;

/** Банк теста: каждая ступень представлена хотя бы двумя вопросами. */
export const PlacementTestSchema = z
  .array(PlacementQuestionSchema)
  .min(PLACEMENT_MIN_QUESTIONS)
  .max(PLACEMENT_MAX_QUESTIONS)
  .superRefine((questions, ctx) => {
    for (const level of CURRICULUM_LEVELS) {
      if (questions.filter((question) => question.level === level).length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Ступени ${level} нужно хотя бы два вопроса` });
      }
    }
  });

/** Вопрос без верного ответа и с подсказкой на языке интерфейса — то, что уходит клиенту. */
export interface PlacementQuestionView extends Omit<PlacementQuestion, 'answerIndex' | 'prompts'> {
  readonly prompt: string;
}

export const PlacementAnswerSchema = z.object({
  questionId: z.string().min(1),
  choiceIndex: z.number().int().nonnegative(),
  responseTimeMs: z.number().int().nonnegative(),
});
export type PlacementAnswer = z.infer<typeof PlacementAnswerSchema>;

export const PlacementSubmitSchema = z.object({
  userId: z.string().uuid(),
  answers: z.array(PlacementAnswerSchema).min(1).max(PLACEMENT_MAX_QUESTIONS),
});
export type PlacementSubmit = z.infer<typeof PlacementSubmitSchema>;

/** Гость (ещё без аккаунта) проходит тест: проверка без сохранения. */
export const PlacementScoreSchema = z.object({
  answers: z.array(PlacementAnswerSchema).min(1).max(PLACEMENT_MAX_QUESTIONS),
});
export type PlacementScore = z.infer<typeof PlacementScoreSchema>;

/** Итоги тестов способностей: три оценки 0–1 (слух может быть пропущен). */
export const AptitudeScoresSchema = z.object({
  memory: z.number().min(0).max(1),
  sound: z.number().min(0).max(1).nullable(),
  grammar: z.number().min(0).max(1),
});
export type AptitudeScoresInput = z.infer<typeof AptitudeScoresSchema>;

/**
 * Итог онбординга, пройденного гостем до входа: после входа он уходит на
 * сервер. Ответы теста сервер проверяет сам — ступень клиент не присылает,
 * её нельзя подменить. Стиль мышления (LLAMA B/F) тоже приходит с клиента,
 * но сервер сам пересчитывает по нему профиль и стартовые веса.
 */
export const CompleteOnboardingSchema = z.object({
  /** zero — «с нуля», placement — прошёл тест, skipped — открыл тест и передумал. */
  path: z.enum(['zero', 'placement', 'skipped']),
  answers: z.array(PlacementAnswerSchema).min(1).max(PLACEMENT_MAX_QUESTIONS).optional(),
  aptitude: AptitudeScoresSchema.optional(),
});
export type CompleteOnboarding = z.infer<typeof CompleteOnboardingSchema>;

export interface PlacementOutcome {
  readonly placedLevel: CurriculumLevel;
  /** Доля верных ответов по всему тесту, [0, 1]. */
  readonly score: number;
  /** Стартовое `estimatedLevel`: середина ступени (A1 → 1.5, B1 → 3.5). */
  readonly estimatedLevel: number;
}

/**
 * Итог теста. Ответы на неизвестные вопросы игнорируются, а вопрос без
 * ответа засчитывается как ошибка — пропуск не помогает подняться выше.
 */
export function scorePlacement(
  questions: readonly PlacementQuestion[],
  answers: readonly PlacementAnswer[],
): PlacementOutcome {
  const chosen = new Map(answers.map((answer) => [answer.questionId, answer.choiceIndex]));
  const isCorrect = (question: PlacementQuestion): boolean => chosen.get(question.id) === question.answerIndex;

  let placedIndex = 0;
  for (const [index, level] of CURRICULUM_LEVELS.entries()) {
    const ofLevel = questions.filter((question) => question.level === level);
    const correct = ofLevel.filter(isCorrect).length;
    const passed = ofLevel.length > 0 && correct >= Math.ceil(ofLevel.length * PLACEMENT_PASS_RATIO);
    if (!passed) break;
    placedIndex = index;
  }

  const total = questions.filter(isCorrect).length;
  return {
    placedLevel: CURRICULUM_LEVELS[placedIndex] ?? 'A1',
    score: questions.length === 0 ? 0 : total / questions.length,
    estimatedLevel: placedIndex + 1.5,
  };
}
